import React, { useEffect, useMemo } from 'react';
import { Box, ShimmerBox, Text } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization, useHistory } from '@/modules/core';
import Link from '@/modules/core/components/Link';

import { usePlanYearActions } from './hooks';
import { useGetPlanListQuery } from './queries';
import TotalSpendBar from './TotalSpendBar';
import TotalSpendDashboardPlaceholder from './TotalSpendDashboardPlaceholder';
import TotalSpendPickerContainer from './TotalSpendPicker.container';

const CURRENT_PLAN_QUERY_PARAM = 'currentPlan';
const TotalSpendDashboardContainer = () => {
  const {
    observingOrganization: { type, id: organizationId },
  } = useCurrentOrganization();

  const isEmployerLevel = useMemo(
    () => type === OrganizationTypes.employer || type === OrganizationTypes.company,
    [type]
  );
  const isSubsidiaryLevel = useMemo(() => type === OrganizationTypes.subsidiary, [type]);
  const history = useHistory();
  useEffect(() => {
    history.deleteQueryParameter([CURRENT_PLAN_QUERY_PARAM]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    currentYear,
    setCurrentYear,
    currentPlanYearTitle,
    isError: isErrorPlanYears,
    isLoading: isLoadingPlanYears,
    options: planYearOptions,
    refetch: refetchPlanYears,
  } = usePlanYearActions({
    organizationId,
    isDisabled: !isEmployerLevel && !isSubsidiaryLevel,
  });
  const {
    isLoading: isLoadingPlanList,
    data: planList,
    isError: isErrorsPlans,
  } = useGetPlanListQuery({
    planYearId: currentYear,
    disabled: (!isEmployerLevel && !isSubsidiaryLevel) || !currentYear,
    organizationId,
  });

  if (!isEmployerLevel && !isSubsidiaryLevel) {
    return null;
  }
  return (
    <Box pad="spacing24" round="moduleRound" background="module">
      <Box
        background="canvas"
        round="container1Round"
        border={{ size: '1px', color: 'border2' }}
        pad="spacing16"
        direction="column"
      >
        <TotalSpendPickerContainer
          value={currentYear}
          onChangeValues={setCurrentYear}
          options={planYearOptions}
          isLoading={isLoadingPlanYears}
        >
          <Text size="medium" weight={700}>
            Total Spend
          </Text>
        </TotalSpendPickerContainer>

        <Box margin={{ top: 'spacing12' }}>
          {isLoadingPlanList || isLoadingPlanYears ? (
            <Box gap="20px">
              <ShimmerBox round="checkboxRound" height="42px" />
              <ShimmerBox round="checkboxRound" height="42px" />
              <ShimmerBox round="checkboxRound" height="42px" />
            </Box>
          ) : (
            <Box gap="20px">
              {planList?.length ? (
                planList?.map((item) => (
                  <Link
                    key={item.id}
                    data-testid={`total-spend-bar_${item.id}`}
                    isOpenNewTab
                    to={ROUTES.ACCOUNT_USAGE_DASHBOARD(item.id, currentYear as string)}
                  >
                    <TotalSpendBar key={item.id} {...item} />
                  </Link>
                ))
              ) : (
                <TotalSpendDashboardPlaceholder
                  isRequestError={isErrorPlanYears || isErrorsPlans}
                  onTryAgain={refetchPlanYears}
                >
                  No spending activity yet {currentPlanYearTitle ? `for ${currentPlanYearTitle || ''}` : ''}
                </TotalSpendDashboardPlaceholder>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TotalSpendDashboardContainer;
